import React, { useState, useEffect } from "react";
import { Star, Clock, MapPin, Sparkles, Users, X } from "lucide-react";
import { ASSETS } from "../data/assets";

const WELCOME_MESSAGE = "Bonjour ! Ravi de vous accueillir. N'hésitez pas à me contacter, je vous réponds personnellement.";
const BUBBLE_AUTO_HIDE_MS = 10000;

export const Hero = () => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!imgLoaded || dismissed) return;
    const showTimer = setTimeout(() => setShowBubble(true), 1000);
    const hideTimer = setTimeout(() => setShowBubble(false), 1000 + BUBBLE_AUTO_HIDE_MS);
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [imgLoaded, dismissed]);

  const handleDismiss = () => {
    setShowBubble(false);
    setDismissed(true);
  };

  return (
    <section
      id="accueil"
      data-testid="hero-section"
      style={{ paddingTop: "calc(8rem + var(--promo-banner-h, 0px))" }}
      className="relative min-h-screen pb-20 overflow-hidden bg-[#0E1A2B]"
    >
      {/* Decorative gold halo */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-40 w-[600px] h-[600px] rounded-full bg-[#C9A869]/8 blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#C47A66]/5 blur-3xl" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Left content */}
        <div className="lg:col-span-7 z-10">
          <div className="flex items-center gap-3 mb-8">
            <span className="h-px w-12 bg-[#C9A869]" />
            <span className="text-[11px] tracking-[0.4em] uppercase text-[#C9A869]">
              France & Luxembourg
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="serif font-light text-[#F7F3EA] text-5xl sm:text-6xl lg:text-7xl xl:text-[80px] leading-[1.15] tracking-tight"
          >
            Cours de danse en
            <br />
            <span className="italic gold-text">France & Luxembourg</span>
          </h1>

          <p className="mt-8 text-base sm:text-lg text-[#F7F3EA]/75 max-w-xl leading-relaxed font-light">
            Valse, rock, rock acrobatique et préparation d'ouverture de bal de mariage. Cours particuliers et collectifs, tous niveaux. Une approche progressive, élégante et bienveillante.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#contact"
              data-testid="hero-cta-reserver"
              className="px-8 py-4 bg-[#C9A869] text-[#0E1A2B] text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-[#DCC28E] transition-colors duration-500"
            >
              Réserver un cours
            </a>
            <a
              href="#avis"
              data-testid="hero-cta-avis"
              className="px-8 py-4 border border-[#F7F3EA]/30 text-[#F7F3EA] text-[12px] tracking-[0.25em] uppercase hover:border-[#C9A869] hover:text-[#C9A869] transition-all duration-500"
            >
              Voir les avis
            </a>
            <a
              href="#mariage"
              data-testid="hero-cta-mariage"
              className="px-8 py-4 border border-[#C47A66]/60 text-[#C47A66] text-[12px] tracking-[0.25em] uppercase hover:bg-[#C47A66] hover:text-[#0E1A2B] transition-all duration-500"
            >
              Préparer mon mariage
            </a>
          </div>
        </div>

        {/* Right photo + welcome bubble */}
        <div className="lg:col-span-5 relative">
          <div className="relative max-w-md mx-auto">
            {/* Welcome bubble
                - Mobile : au-dessus de la photo (in-flow avec mb-5), jamais sur le visage
                - Desktop : absolue, chevauche le coin haut-gauche du cadre */}
            {showBubble && (
              <div
                data-testid="hero-welcome-bubble"
                role="status"
                aria-live="polite"
                className="relative mb-5 lg:mb-0 lg:absolute lg:-top-6 lg:-left-14 lg:z-30 max-w-[300px] mx-auto lg:mx-0 animate-bubble-in"
              >
                <div className="relative bg-[#F7F3EA] text-[#0E1A2B] px-5 py-4 pr-9 shadow-[0_12px_35px_rgba(0,0,0,0.35)] border border-[#C9A869]">
                  <p className="serif italic text-base leading-snug">
                    {WELCOME_MESSAGE}
                  </p>
                  <p className="mt-2 text-[10px] tracking-[0.25em] uppercase text-[#C9A869] font-semibold not-italic">
                    — Louis-Melchior
                  </p>

                  <button
                    onClick={handleDismiss}
                    data-testid="hero-welcome-bubble-close"
                    aria-label="Fermer le message"
                    className="absolute top-1.5 right-1.5 p-1 text-[#0E1A2B]/50 hover:text-[#0E1A2B] transition-colors"
                  >
                    <X size={13} />
                  </button>

                  {/* Bubble tail — pointe vers la photo (bas) sur mobile et desktop */}
                  <span
                    aria-hidden
                    className="absolute -bottom-2 right-10 lg:right-8 w-4 h-4 bg-[#F7F3EA] border-r border-b border-[#C9A869] rotate-45"
                  />
                </div>
              </div>
            )}

            <div className="relative aspect-[3/4]">
              <div className="absolute -inset-4 border border-[#C9A869]/40" />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 border-2 border-[#C9A869]/60" />
              <img
                src={ASSETS.louisHero}
                alt="Louis-Melchior, professeur de danse en France et au Luxembourg"
                data-testid="hero-photo"
                onLoad={() => setImgLoaded(true)}
                className="relative w-full h-full object-cover grayscale-[10%]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0E1A2B]/40 via-transparent to-transparent pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Reassurance badges */}
      <div className="relative mt-20 max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="border-t border-b border-[#C9A869]/20 py-7 grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-3">
          <Badge icon={<Star size={16} />} title="5/5" subtitle="10 avis" />
          <Badge icon={<Clock size={16} />} title="Réponse" subtitle="sous 6h" />
          <Badge icon={<Users size={16} />} title="Tous" subtitle="niveaux" />
          <Badge icon={<Sparkles size={16} />} title="Domicile" subtitle="ou lieu public" />
          <Badge icon={<MapPin size={16} />} title="Partout" subtitle="France & Luxembourg" />
        </div>
      </div>
    </section>
  );
};

const Badge = ({ icon, title, subtitle }) => (
  <div className="flex items-center gap-3" data-testid={`badge-${title.toLowerCase().replace(/[^a-z0-9]/gi, "-")}`}>
    <div className="w-9 h-9 flex items-center justify-center border border-[#C9A869]/40 text-[#C9A869]">
      {icon}
    </div>
    <div className="leading-tight">
      <div className="text-sm text-[#F7F3EA] font-medium">{title}</div>
      <div className="text-[11px] tracking-[0.18em] uppercase text-[#F7F3EA]/55">{subtitle}</div>
    </div>
  </div>
);
